import classNames from 'classnames';
import { treemap as d3Treemap, hierarchy, treemapSquarify } from 'd3';
import { scaleOrdinal } from 'd3-scale';
import { truncate } from 'lodash';
import { Fragment, type KeyboardEvent } from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, plotColors } from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { useTooltipController } from '../plot-tooltip';
import styles from './treemap.module.css';
import { TreeMapTooltip } from './treemap-tooltip';

export type TreeMapNode = {
  name: string;
  value: number;
  children?: TreeMapNode[];
  color?: string;
  parentName?: string;
  id?: string;
};

export type TreeMapData = {
  name: string;
  children: TreeMapNode[];
};

type TreeDatum = {
  name: string;
  value?: number;
  color?: string;
  parentName?: string;
  children?: TreeDatum[];
  id?: string;
};

export type BreadcrumbSegment = {
  name: string;
  targetDepth: number;
};

export type TreeMapProps = {
  data: TreeMapData;
  colorPalette?: string[];
  colorOverride?: Record<string, string>;
  onCellClick?: (cellId: string, cellName: string, parentName?: string) => void;
  onHeaderClick?: (targetDepth: number) => void;
  breadcrumbPath?: BreadcrumbSegment[];
  sizeTransform?: 'none' | 'sqrt';
} & CommonPlotProps;

function getOverrideColor(
  name: string,
  colorOverride: Record<string, string> | undefined
): string | undefined {
  if (colorOverride === undefined) return undefined;
  const key = Object.keys(colorOverride).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );
  return key !== undefined ? colorOverride[key] : undefined;
}

function isOtherDatum(datum: { id?: string; name?: string }): boolean {
  if (datum.id === '-1') return true;
  return (datum.name ?? '').toLowerCase() === 'other';
}

export const TreeMap = ({
  data,
  loading = false,
  format = FormatType.SI,
  sizeTransform = 'none',
  colorPalette: colorPaletteProp,
  colorOverride,
  onCellClick,
  onHeaderClick,
  breadcrumbPath,
}: TreeMapProps) => {
  const clickable = onCellClick !== undefined;
  const overridenColors = Object.values(colorOverride ?? {});
  const basePalette = colorPaletteProp?.length ? colorPaletteProp : plotColors;
  const colorPalette = basePalette.filter(
    (color) => !overridenColors.includes(color)
  );
  const { containerRef, width, height } = useResizeObserver();

  const isPlotSizeValid = width > 0 && height > 0;
  const hasData = Array.isArray(data?.children) && data.children.length > 0;

  function toTreeDatum(node: TreeMapNode): TreeDatum {
    const children = Array.isArray(node.children)
      ? node.children.map((child) => toTreeDatum(child))
      : undefined;
    return {
      name: node.name,
      value: typeof node.value === 'number' ? node.value : undefined,
      color: node.color,
      parentName: node.parentName,
      children,
      id: node.id,
    };
  }

  const rootDatum: TreeDatum = {
    name: data.name,
    children: data.children.map((c) => toTreeDatum(c)),
  };

  const root = hierarchy<TreeDatum>(rootDatum)
    .sum((d) => {
      if (d.children && d.children.length) return 0;
      const rawValue = Math.max(0, d.value ?? 0);
      if (sizeTransform === 'sqrt') return Math.sqrt(rawValue);
      return rawValue;
    })
    .sort((a, b) => {
      const aIsOther = isOtherDatum(a.data);
      const bIsOther = isOtherDatum(b.data);
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return (b.value ?? 0) - (a.value ?? 0);
    });

  const headerHeight = 19;
  const gutterHeight = 2;
  const headerAreaHeight = headerHeight + gutterHeight;
  const hasBreadcrumb = breadcrumbPath != null && breadcrumbPath.length > 0;
  const treemapHeight = hasBreadcrumb
    ? Math.max(0, height - headerAreaHeight)
    : height;

  const treemapRoot = d3Treemap<TreeDatum>()
    .tile(treemapSquarify)
    .size([Math.max(0, width), Math.max(0, treemapHeight)])
    .paddingInner(2)
    .paddingTop((node) => {
      if (hasBreadcrumb) return 0;
      if (node.children && node.depth > 0) return headerAreaHeight + 2;
      return 0;
    })
    .round(true)(root);

  const leaves = treemapRoot.leaves();
  const totalRawSum = leaves.reduce((sum, leaf) => {
    const rawValue = typeof leaf.data.value === 'number' ? leaf.data.value : 0;
    return sum + rawValue;
  }, 0);
  const groupsWithChildren = hasBreadcrumb
    ? []
    : treemapRoot
        .descendants()
        .filter(
          (n) =>
            n.depth > 0 && Array.isArray(n.children) && n.children.length > 0
        );

  const isFlatData = groupsWithChildren.length === 0 && leaves.length > 0;

  const inputFirstLevel = data.children.map((c) => c.name);
  const leafNames = leaves.map((l) => l.data.name);
  const colorDomain =
    inputFirstLevel.length > 1 ? inputFirstLevel : [...new Set(leafNames)];
  const colorScale = scaleOrdinal<string, string>()
    .domain(colorDomain)
    .range(colorPalette);

  const useSingleColor = colorPalette.length === 1;
  const singleColor = colorPalette[0];
  const colorByLeaf = inputFirstLevel.length === 1 && leaves.length > 1;

  const tooltipController = useTooltipController({ onHover: () => undefined });
  const formatValue = getFormatter(format);

  return (
    <div
      className={classNames(styles.container)}
      data-testid={plotTestId(ReplotChartNames.TreeMap)}
    >
      <div ref={containerRef} className={styles.sizingBox}>
        <PlotLoadWrapper loading={loading} noData={!hasData}>
          <svg width="100%" height="100%">
            {isPlotSizeValid && (
              <g {...tooltipController.gProps}>
                {hasBreadcrumb && (
                  <g>
                    <rect
                      x={0}
                      y={0}
                      width={width}
                      height={headerHeight}
                      fill="var(--ui-text-normal, #2d426a)"
                      className={styles.headerRect}
                    />
                    <rect
                      x={0}
                      y={headerHeight}
                      width={width}
                      height={gutterHeight}
                      fill="#ffffff"
                    />
                    <text x={6} y={13} className={styles.breadcrumbLabel}>
                      {(breadcrumbPath ?? []).map((seg, i) => {
                        const handleClick = () => {
                          onHeaderClick?.(seg.targetDepth);
                        };
                        const handleKeyDown = (e: KeyboardEvent<Element>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                          }
                        };
                        return (
                          <Fragment key={`breadcrumb-${seg.targetDepth}`}>
                            {i > 0 && <tspan> &gt; </tspan>}
                            <tspan
                              onClick={handleClick}
                              onKeyDown={handleKeyDown}
                              role="button"
                              tabIndex={0}
                              className={styles.breadcrumbLink}
                              style={{ cursor: 'pointer' }}
                            >
                              {truncate(seg.name, { length: 50 })}
                            </tspan>
                          </Fragment>
                        );
                      })}
                    </text>
                  </g>
                )}
                {!hasBreadcrumb &&
                  groupsWithChildren.map((groupNode) => {
                    const x = groupNode.x0;
                    const y = groupNode.y0 + 2;
                    const w = Math.max(0, groupNode.x1 - groupNode.x0);
                    const h = headerHeight;
                    if (w <= 0 || h <= 0) return null;

                    let topAncestor = groupNode;
                    while (topAncestor.parent && topAncestor.parent.depth > 0) {
                      topAncestor = topAncestor.parent;
                    }
                    const title = String(groupNode.data.name ?? '');
                    const labelMaxChars = Math.floor((w - 10) / 7);
                    const targetDepth = Math.max(0, groupNode.depth - 1);
                    const handleHeaderClick = () => {
                      onHeaderClick?.(targetDepth);
                    };
                    const handleHeaderKeyDown = (e: KeyboardEvent<Element>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleHeaderClick();
                      }
                    };

                    return (
                      <g
                        key={`header-${title}-${groupNode.x0}-${groupNode.y0}`}
                      >
                        <rect
                          x={x}
                          y={y}
                          width={w}
                          height={h}
                          fill="var(--ui-text-normal, #2d426a)"
                          className={styles.headerRect}
                          style={{
                            ...(onHeaderClick ? { cursor: 'pointer' } : {}),
                          }}
                          onClick={
                            onHeaderClick ? handleHeaderClick : undefined
                          }
                          onKeyDown={
                            onHeaderClick ? handleHeaderKeyDown : undefined
                          }
                          role={onHeaderClick ? 'button' : undefined}
                          tabIndex={onHeaderClick ? 0 : undefined}
                        />
                        {w > 40 && labelMaxChars >= 4 && (
                          <text
                            x={x + 6}
                            y={y + 13}
                            className={styles.headerLabel}
                          >
                            {truncate(title, {
                              length: Math.max(0, labelMaxChars),
                            })}
                          </text>
                        )}
                      </g>
                    );
                  })}
                {leaves.map((leaf, idx) => {
                  const x = leaf.x0;
                  const y = leaf.y0 + (hasBreadcrumb ? headerAreaHeight : 0);
                  const w = Math.max(0, leaf.x1 - leaf.x0);
                  const h = Math.max(0, leaf.y1 - leaf.y0);
                  if (w <= 0 || h <= 0) return null;

                  let group = leaf;
                  while (group.parent && group.parent.depth > 0) {
                    group = group.parent;
                  }
                  const groupName = group.data.name;
                  const colorKey = colorByLeaf ? leaf.data.name : groupName;
                  const fill =
                    leaf.data.color ??
                    getOverrideColor(leaf.data.name, colorOverride) ??
                    (useSingleColor ? singleColor : colorScale(colorKey));

                  const name = String(leaf.data.name ?? '');
                  const parentName = String(
                    leaf.data.parentName ?? leaf.parent?.data.name ?? ''
                  );
                  const displayNumericValue = (() => {
                    if (typeof leaf.data.value !== 'number') return undefined;
                    if (format === FormatType.PERCENTAGE) {
                      if (totalRawSum === 0) return 0;
                      return leaf.data.value / totalRawSum;
                    }
                    return leaf.data.value;
                  })();
                  const valueLabel =
                    displayNumericValue === undefined
                      ? '-'
                      : formatValue(displayNumericValue);

                  const shouldShowParent = !isFlatData && parentName;
                  const titleText = shouldShowParent
                    ? `${parentName} / ${name}`
                    : name;

                  const cellId = String(leaf.data.id ?? name);
                  const handleClick = () => {
                    if (clickable) {
                      onCellClick?.(cellId, name, parentName || undefined);
                    }
                  };
                  const handleKeyDown = (e: KeyboardEvent<Element>) => {
                    if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleClick();
                    }
                  };

                  return (
                    <g key={`${name}-${idx}`}>
                      <TreeMapTooltip
                        title={String(titleText)}
                        entries={[{ label: 'Value', value: valueLabel }]}
                      >
                        <rect
                          x={x}
                          y={y}
                          width={w}
                          height={h}
                          fill={fill}
                          className={styles.nodeRect}
                          onClick={clickable ? handleClick : undefined}
                          onKeyDown={clickable ? handleKeyDown : undefined}
                          style={clickable ? { cursor: 'pointer' } : undefined}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                        />
                      </TreeMapTooltip>
                      {w > 40 && h > 26 && (
                        <g>
                          {Math.floor(w / 7) >= 4 && (
                            <text
                              x={x + 6}
                              y={y + 14}
                              className={styles.nodeLabel}
                            >
                              {truncate(name, {
                                length: Math.floor(w / 7),
                              })}
                            </text>
                          )}
                          <text
                            x={x + 6}
                            y={y + 26}
                            className={styles.nodeValueLabel}
                          >
                            {valueLabel}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            )}
          </svg>
        </PlotLoadWrapper>
      </div>
    </div>
  );
};
